/**
 * Bid Automation V2 — Sponsored Products (SP) min/max bid by marketplace country.
 * Source: Amazon Ads hard floors/ceilings (SP column only).
 * Resolve via clientData → amazon_setup.profile_info.countryCode.
 */

import {
  getAmazonCountryCodeFromClientData,
  getAmazonCurrencyCodeFromClientData,
  CAMPAIGN_LIST_V2_COUNTRY_TO_CURRENCY,
  CAMPAIGN_LIST_V2_DEFAULT_CURRENCY_CODE,
} from "../../campaignList/campaignListV2BudgetConfig";

/** Fallback when country is unknown (India SP). */
export const BID_V2_DEFAULT_COUNTRY_CODE = "IN";

/**
 * SP min/max bid by marketplace country code.
 * Only SP values are stored (SD/SB/SBV ignored for Bid Automation V2 caps).
 * minBid aligned with Targeting V2 currency floors.
 * scale: 0 = integers only (JP/ZA, same as Targeting V2); 2 = up to 2 decimals.
 */
export const BID_V2_SP_BID_RULES_BY_COUNTRY = {
  MX: { minBid: 0.1, maxBid: 20000, currencyCode: "MXN", scale: 2 },
  UK: { minBid: 0.02, maxBid: 1000, currencyCode: "GBP", scale: 2 },
  GB: { minBid: 0.02, maxBid: 1000, currencyCode: "GBP", scale: 2 },
  DE: { minBid: 0.02, maxBid: 1000, currencyCode: "EUR", scale: 2 },
  CA: { minBid: 0.02, maxBid: 1000, currencyCode: "CAD", scale: 2 },
  AU: { minBid: 0.02, maxBid: 1410, currencyCode: "AUD", scale: 2 },
  US: { minBid: 0.02, maxBid: 1000, currencyCode: "USD", scale: 2 },
  FR: { minBid: 0.02, maxBid: 1000, currencyCode: "EUR", scale: 2 },
  ES: { minBid: 0.02, maxBid: 1000, currencyCode: "EUR", scale: 2 },
  IT: { minBid: 0.02, maxBid: 1000, currencyCode: "EUR", scale: 2 },
  NL: { minBid: 0.02, maxBid: 1000, currencyCode: "EUR", scale: 2 },
  JP: { minBid: 2, maxBid: 100000, currencyCode: "JPY", scale: 0 },
  AE: { minBid: 0.1, maxBid: 184, currencyCode: "AED", scale: 2 },
  UAE: { minBid: 0.1, maxBid: 184, currencyCode: "AED", scale: 2 },
  BR: { minBid: 0.07, maxBid: 3700, currencyCode: "BRL", scale: 2 },
  SG: { minBid: 0.02, maxBid: 1100, currencyCode: "SGD", scale: 2 },
  SE: { minBid: 0.18, maxBid: 9300, currencyCode: "SEK", scale: 2 },
  IN: { minBid: 1, maxBid: 5000, currencyCode: "INR", scale: 2 },
  PL: { minBid: 0.04, maxBid: 2000, currencyCode: "PLN", scale: 2 },
  TR: { minBid: 0.05, maxBid: 2500, currencyCode: "TRY", scale: 2 },
  EG: { minBid: 0.15, maxBid: 5.5, currencyCode: "EGP", scale: 2 },
  SA: { minBid: 0.1, maxBid: 3670, currencyCode: "SAR", scale: 2 },
  BE: { minBid: 0.02, maxBid: 1000, currencyCode: "EUR", scale: 2 },
  ZA: { minBid: 1, maxBid: 7000, currencyCode: "ZAR", scale: 0 },
  IE: { minBid: 0.02, maxBid: 1000, currencyCode: "EUR", scale: 2 },
};

export function resolveBidV2CountryCode(countryCode) {
  const code = String(
    countryCode || getAmazonCountryCodeFromClientData() || ""
  )
    .trim()
    .toUpperCase();

  if (code && Object.prototype.hasOwnProperty.call(BID_V2_SP_BID_RULES_BY_COUNTRY, code)) {
    return code === "GB" ? "UK" : code === "UAE" ? "AE" : code;
  }

  return BID_V2_DEFAULT_COUNTRY_CODE;
}

export function getSpBidRule(countryCode) {
  const resolved = resolveBidV2CountryCode(countryCode);
  const rule =
    BID_V2_SP_BID_RULES_BY_COUNTRY[resolved] ||
    BID_V2_SP_BID_RULES_BY_COUNTRY[BID_V2_DEFAULT_COUNTRY_CODE];

  const currencyFromProfile = getAmazonCurrencyCodeFromClientData();
  const currencyCode =
    rule.currencyCode ||
    currencyFromProfile ||
    CAMPAIGN_LIST_V2_COUNTRY_TO_CURRENCY[resolved] ||
    CAMPAIGN_LIST_V2_DEFAULT_CURRENCY_CODE;

  return {
    countryCode: resolved,
    currencyCode,
    minBid: rule.minBid,
    maxBid: rule.maxBid,
    scale: rule.scale ?? 2,
  };
}

export function getSpBidScale(countryCode) {
  return getSpBidRule(countryCode).scale;
}

export function getMinSpBid(countryCode) {
  return getSpBidRule(countryCode).minBid;
}

export function getMaxSpBid(countryCode) {
  return getSpBidRule(countryCode).maxBid;
}

export function getSpBidCurrencyCode(countryCode) {
  return getSpBidRule(countryCode).currencyCode;
}

/** Format bid bound for error copy (trim trailing zeros). */
export function formatSpBidBound(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value ?? "");
  return String(num);
}
